import { Link, useParams } from "react-router-dom";
import { ArrowLeft, User, XCircle } from "lucide-react";
import { usePageMeta } from "@/lib/usePageMeta";
import { useApi } from "@/lib/api";
import { toEvent } from "@/lib/mappers";
import { fmtDay, fmtTime, fmtDate } from "@/lib/format";
import { PendingAction, EmptyState, ArrowLink, LoadingState, ErrorState } from "@/components/site/Content";
import { Markdown } from "@/components/site/Markdown";
import { DateBlock, RegistrationBadge } from "@/components/site/EventCard";
import { ShareBar } from "@/components/site/SubscribeButton";
import { useCopy } from "@/lib/copy";

const Info = ({ label, children }) => (
  <div className="py-4">
    <dt className="eyebrow text-ink-muted">{label}</dt>
    <dd className="mt-1 font-semibold text-ink">{children}</dd>
  </div>
);

const ExtLink = ({ href, children, testId, primary }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className={`inline-flex rounded-full px-6 py-3 text-sm font-bold ${primary ? "bg-brand text-white hover:bg-[#0B3D8A]" : "border border-brand text-brand hover:bg-brand hover:text-white"}`} data-testid={testId}>
    {children}
  </a>
);

export const isHttpUrl = (u) => {
  try {
    return ["http:", "https:"].includes(new URL(u).protocol);
  } catch {
    return false;
  }
};

const Registration = ({ ev, past, cancelled, closed, t }) => {
  const official = isHttpUrl(ev.officialEventUrl);
  const register = isHttpUrl(ev.registrationUrl) && !closed && !past && !cancelled;
  return (
    <div className="space-y-4">
      {cancelled && <PendingAction label="Event cancelled" note="This event has been cancelled by the organiser." testId="event-register-pending" />}
      {past && !cancelled && <PendingAction label="Event has ended" note="Registration is no longer available for this event." testId="event-register-pending" />}
      {!past && !cancelled && closed && <PendingAction label="Registration closed" note="The registration deadline for this event has passed." testId="event-register-pending" />}
      {(official || register) && (
        <div className="flex flex-wrap gap-3">
          {official && <ExtLink href={ev.officialEventUrl} primary testId="event-official-link">{t("events.detail.official")}<span className="sr-only"> (opens in a new tab)</span></ExtLink>}
          {register && <ExtLink href={ev.registrationUrl} testId="event-register-link">{t("events.detail.register")}<span className="sr-only"> (opens in a new tab)</span></ExtLink>}
        </div>
      )}
      <ShareBar kind="events" slug={ev.slug} t={t} />
    </div>
  );
};

export default function EventDetail() {
  const t = useCopy();
  const { slug } = useParams();
  const { data, loading, error } = useApi(`/events/${encodeURIComponent(slug)}`);
  const ev = data && toEvent(data);
  usePageMeta(ev ? ev.title : loading ? "Loading event" : "Event not found", ev?.shortSummary);

  if (loading) return <div className="container-x py-20"><LoadingState className="h-[60vh]" label="Loading event" /></div>;
  if (error === "error") return <div className="container-x py-20"><ErrorState testId="event-error" /></div>;
  if (!ev) {
    return (
      <div className="container-x py-20">
        <EmptyState title="Event not found" message="This event may have been removed or is not yet listed." testId="event-not-found" action={<ArrowLink to="/events" testId="event-not-found-link">Browse all events</ArrowLink>} />
      </div>
    );
  }

  const cancelled = ev.status === "Cancelled";
  const past = ev.section === "past";
  const closed = ev.deadline ? ev.deadline < new Date() : false;

  return (
    <article data-testid="event-detail">
      <header className="border-b border-line">
        <div className="container-x py-10 md:py-14">
          <Link to="/events" className="inline-flex items-center gap-2 text-sm font-bold text-brand hover:underline" data-testid="event-back-link">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> {t("events.detail.back")}
          </Link>
          <div className="mt-10 flex gap-6">
            <div className="hidden border border-line sm:block"><DateBlock date={ev.start} /></div>
            <div className="max-w-4xl">
              <div className="flex flex-wrap items-center gap-3">
                {ev.type && <span className="chip">{ev.type}</span>}
                {ev.domains.map((d) => <span key={d} className="chip chip-green">{d}</span>)}
                {past && !cancelled && <span className="chip" data-testid="event-past-label">Past event</span>}
                {!past && !cancelled && <RegistrationBadge event={ev} />}
                {cancelled && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-[#B42318] px-3 py-1 text-xs font-bold text-[#B42318]" data-testid="event-cancelled-label">
                    <XCircle className="h-3.5 w-3.5" aria-hidden="true" /> Cancelled
                  </span>
                )}
              </div>
              <h1 className="mt-5 text-4xl font-extrabold leading-[1.08] tracking-tight text-brand sm:text-5xl" data-testid="event-title">{ev.title}</h1>
              <p className="mt-5 max-w-2xl text-lg leading-relaxed text-ink-muted" data-testid="event-summary">{ev.shortSummary}</p>
            </div>
          </div>
        </div>
      </header>

      <div className="container-x grid gap-12 py-14 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <h2 className="text-xl font-extrabold text-brand">{t("events.detail.about")}</h2>
          <Markdown className="mt-4 max-w-[68ch] space-y-5 text-base leading-[1.8] text-ink md:text-lg" testId="event-description">{ev.description}</Markdown>

          {ev.speakers.length > 0 && (
            <>
              <h2 className="mt-12 text-xl font-extrabold text-brand">Speakers</h2>
              <ul className="mt-5 grid gap-4 sm:grid-cols-2" data-testid="event-speakers">
                {ev.speakers.map((sp, i) => (
                  <li key={i} className="flex items-center gap-4 border border-line p-4">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-pale text-brand" aria-hidden="true"><User className="h-5 w-5" /></span>
                    <div><p className="font-bold text-ink">{sp.name}</p>{sp.role && <p className="text-sm text-ink-muted">{sp.role}</p>}</div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <aside className="space-y-6 lg:col-span-5" aria-label="Event details">
          <dl className="divide-y divide-line border-y border-line" data-testid="event-info">
            <Info label="Date">{fmtDay(ev.start)}</Info>
            <Info label="Time">{fmtTime(ev.start)}{ev.end && ` – ${fmtTime(ev.end)}`} IST</Info>
            {ev.end && fmtDate(ev.end) !== fmtDate(ev.start) && <Info label="Ends">{fmtDay(ev.end)}</Info>}
            {ev.mode && <Info label="Mode">{ev.mode}</Info>}
            {ev.venue && <Info label="Venue">{ev.venue}{ev.venueAddress && <span className="mt-1 block font-normal text-ink-muted">{ev.venueAddress}</span>}</Info>}
            {ev.online && <Info label="Online joining">{ev.online}</Info>}
            {ev.organiser && <Info label="Organiser">{ev.organiser}</Info>}
            {ev.fee && <Info label="Fee">{ev.fee}</Info>}
            {ev.intendedAudience && <Info label="Intended audience">{ev.intendedAudience}</Info>}
            {ev.mood && <Info label="Mood">{ev.mood}</Info>}
            {ev.deadline && (
              <Info label="Registration deadline">
                {fmtDate(ev.deadline)} {closed && <span className="ml-2 text-sm font-semibold text-ink-muted">(closed)</span>}
              </Info>
            )}
          </dl>
          <Registration ev={ev} past={past} cancelled={cancelled} closed={closed} t={t} />
        </aside>
      </div>
    </article>
  );
}
